import React from 'react';

export interface DropdownOption {
    value: string;
    label: string;
}

interface DropdownSelectProps {
    /** Form field name submitted with the surrounding <form> (FormData / native submit). */
    name: string;
    options: DropdownOption[];
    /** Text shown while nothing is selected. */
    placeholder: string;
    id?: string;
    required?: boolean;
    /** Initial value (uncontrolled). */
    defaultValue?: string;
    onChange?: (value: string) => void;
}

/**
 * Themeable drop-in for a native <select> (a native option list can't be styled).
 * Form-compatible: a hidden input carries `name`/value and native `required` validation.
 * Follows the WAI-ARIA "select-only combobox" pattern. Styles: `.eb-dropdown-select*` in login-new-theme.css.
 */
const DropdownSelect: React.FC<DropdownSelectProps> = ({
    name, options, placeholder, id, required, defaultValue = '', onChange,
}) => {
    const [value, setValue] = React.useState(defaultValue);
    const [open, setOpen] = React.useState(false);
    // Option under the mouse/keyboard cursor; null = "follow the selected option" (or the first one).
    const [cursor, setCursor] = React.useState<number | null>(null);
    const rootRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const listId = React.useId();

    const selectedIndex = options.findIndex((o) => o.value === value);
    const active = cursor ?? Math.max(0, selectedIndex);

    const close = () => {
        setOpen(false);
        setCursor(null);
    };

    const select = (index: number) => {
        setValue(options[index].value);
        onChange?.(options[index].value);
        close();
    };

    // Close on outside click (the list is not a portal, so a root-contains check is enough).
    React.useEffect(() => {
        if (!open) return;
        const onOutside = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) close();
        };
        document.addEventListener('mousedown', onOutside);
        return () => document.removeEventListener('mousedown', onOutside);
    }, [open]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' || e.key === 'Tab') return close(); // Tab must keep its default (move focus)
        if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) return;
        e.preventDefault(); // stop page scroll on arrows/space and implicit form submit on Enter
        if (!open) return setOpen(true);
        if (e.key === 'Enter' || e.key === ' ') return select(active);
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setCursor((active + step + options.length) % options.length); // wrap around
    };

    return (
        <div className="eb-dropdown-select" ref={rootRef}>
            {/* Hidden required input: carries name/value for FormData and drives native validation. */}
            <input className="eb-dropdown-select-input" id={id} name={name} value={value}
                   required={required} tabIndex={-1} autoComplete="off" aria-hidden="true"
                   onChange={() => undefined} onFocus={() => triggerRef.current?.focus()}/>
            <button type="button" ref={triggerRef} className="form-control form-select eb-dropdown-select-trigger"
                    role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-controls={listId}
                    aria-activedescendant={open ? `${listId}-${active}` : undefined}
                    onClick={() => (open ? close() : setOpen(true))} onKeyDown={onKeyDown}>
                {options[selectedIndex]?.label ?? placeholder}
            </button>
            {open && (
                <ul className="eb-dropdown-select-menu" id={listId} role="listbox">
                    {options.map((o, i) => (
                        <li key={o.value} id={`${listId}-${i}`} role="option" aria-selected={i === selectedIndex}
                            className={'eb-dropdown-select-option' + (i === active ? ' active' : '')}
                            onMouseEnter={() => setCursor(i)}
                            // mousedown (not click) so selection happens before the trigger loses focus
                            onMouseDown={(e) => { e.preventDefault(); select(i); }}>
                            {o.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default DropdownSelect;
